/**
 * Somewhere to work things out, on screen, during the exam.
 *
 * Rough work used to happen on paper and be photographed afterwards. That step
 * is gone, which left the student with nowhere to count a loop or write out a
 * string before answering — so this is where that goes now.
 *
 * **Nothing here is uploaded, and that is the design, not a shortcut.** The
 * screen is already being recorded, so the working is ALREADY captured — in
 * context, at the moment it was written, next to the question it belongs to.
 * Uploading it again would be a second copy of something the reviewer can
 * already watch, paid for on exactly the connection this app spends its time
 * trying not to saturate. It lives in `sessionStorage` for the sitting and
 * goes no further: not to the server, not to another tab, not to another
 * attempt.
 *
 * Two tabs rather than one clever surface. Typing into a canvas means
 * hit-testing, carets and text editing nobody asked for; a textarea is better
 * at writing than any canvas, and a canvas is better at drawing than any
 * textarea. Switching between them costs one click and keeps both good.
 *
 * The drawing is stored as strokes in NORMALISED coordinates, not as pixels,
 * so resizing the panel keeps the drawing instead of cropping or blurring it —
 * and it makes undo a matter of dropping the last stroke.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Eraser,
  GripVertical,
  Minus,
  Pencil,
  Trash2,
  Type,
  Undo2,
} from "lucide-react";
import { cn } from "@/lib/cn";

type Tab = "write" | "draw";

/** A point in 0..1 of the canvas, so it survives a resize. */
interface Point {
  x: number;
  y: number;
}

interface Stroke {
  points: Point[];
  colour: string;
  /** Relative to the canvas width, so a line keeps its weight when resized. */
  width: number;
  erase: boolean;
}

interface Placement {
  x: number;
  y: number;
  w: number;
  h: number;
}

const MIN_W = 260;
const MIN_H = 220;

/** Enough to tell working apart at a glance; more is a colour picker nobody needs. */
const COLOURS = ["#1c1917", "#1d4ed8", "#b91c1c", "#15803d"] as const;

const PEN_WIDTH = 0.004;
const ERASER_WIDTH = 0.05;

interface Saved {
  tab: Tab;
  notes: string;
  strokes: Stroke[];
  placement: Placement;
  open: boolean;
}

/**
 * Keyed by attempt, so nothing written during one sitting can appear in
 * another — including a set of notes typed before an exam the student then
 * restarted.
 */
const storageKey = (attemptId: string) => `ep_scratch_${attemptId}`;

function load(attemptId: string): Partial<Saved> {
  try {
    const raw = sessionStorage.getItem(storageKey(attemptId));
    return raw ? (JSON.parse(raw) as Partial<Saved>) : {};
  } catch {
    // Private mode, blocked storage, corrupted JSON. An empty pad is a fine
    // outcome; refusing to render one is not.
    return {};
  }
}

function defaultPlacement(): Placement {
  // Bottom-left: the question is on the left of the runner and the palette and
  // Submit are on the right, so this is the corner with least to cover. It is
  // draggable anyway — this only has to be a reasonable place to start.
  const w = Math.min(420, Math.max(MIN_W, window.innerWidth * 0.28));
  const h = Math.min(360, Math.max(MIN_H, window.innerHeight * 0.42));
  return { x: 24, y: Math.max(16, window.innerHeight - h - 24), w, h };
}

export function Scratchpad({ attemptId }: { attemptId: string }) {
  const saved = useRef<Partial<Saved>>(load(attemptId)).current;

  const [open, setOpen] = useState(saved.open ?? false);
  const [tab, setTab] = useState<Tab>(saved.tab ?? "write");
  const [notes, setNotes] = useState(saved.notes ?? "");
  const [strokes, setStrokes] = useState<Stroke[]>(saved.strokes ?? []);
  const [placement, setPlacement] = useState<Placement>(
    saved.placement ?? defaultPlacement(),
  );
  const [colour, setColour] = useState<string>(COLOURS[0]);
  const [erasing, setErasing] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef<Stroke | null>(null);

  /* -- persistence --------------------------------------------------- */

  useEffect(() => {
    try {
      sessionStorage.setItem(
        storageKey(attemptId),
        JSON.stringify({ tab, notes, strokes, placement, open } satisfies Saved),
      );
    } catch {
      // Storage full or blocked. The pad keeps working for this page view;
      // losing it on refresh is better than losing the exam to an exception.
    }
  }, [attemptId, tab, notes, strokes, placement, open]);

  /* -- drawing ------------------------------------------------------- */

  /** Repaints every stroke. Cheap enough at the scale one person can draw. */
  const repaint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const { width, height } = canvas.getBoundingClientRect();
    if (width === 0 || height === 0) return;

    // Backing store in device pixels, drawing in CSS pixels — otherwise the
    // ink is soft on every retina screen.
    if (canvas.width !== Math.round(width * dpr)) {
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const all = drawing.current ? [...strokes, drawing.current] : strokes;
    for (const stroke of all) {
      if (stroke.points.length === 0) continue;
      ctx.beginPath();
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = Math.max(1, stroke.width * width);
      ctx.strokeStyle = stroke.colour;
      // `destination-out` clears rather than painting, so the eraser works
      // whatever colour the panel is and never leaves a white smear on a
      // dark theme. Its `colour` is therefore unused.
      ctx.globalCompositeOperation = stroke.erase
        ? "destination-out"
        : "source-over";

      const [first, ...rest] = stroke.points;
      ctx.moveTo(first!.x * width, first!.y * height);
      if (rest.length === 0) {
        // A dot: a zero-length line draws nothing with a round cap.
        ctx.lineTo(first!.x * width + 0.01, first!.y * height);
      }
      for (const p of rest) ctx.lineTo(p.x * width, p.y * height);
      ctx.stroke();
    }
    ctx.globalCompositeOperation = "source-over";
  }, [strokes]);

  useEffect(() => {
    if (open && tab === "draw") repaint();
  }, [open, tab, placement, repaint]);

  const pointFrom = (e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const r = e.currentTarget.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) / r.width,
      y: (e.clientY - r.top) / r.height,
    };
  };

  const startStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = {
      points: [pointFrom(e)],
      colour,
      width: erasing ? ERASER_WIDTH : PEN_WIDTH,
      erase: erasing,
    };
    repaint();
  };

  const extendStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    drawing.current.points.push(pointFrom(e));
    repaint();
  };

  const endStroke = () => {
    const stroke = drawing.current;
    drawing.current = null;
    if (stroke && stroke.points.length > 0) setStrokes((s) => [...s, stroke]);
  };

  /* -- drag and resize ----------------------------------------------- */

  const gesture = useRef<{
    kind: "move" | "resize";
    startX: number;
    startY: number;
    from: Placement;
  } | null>(null);

  const beginGesture = (kind: "move" | "resize") => (e: React.PointerEvent) => {
    e.preventDefault();
    // The gesture is recorded BEFORE the capture is attempted. Capturing can
    // throw (a pointer that is no longer active, a synthetic event), and doing
    // it first meant the throw aborted the handler and the drag silently never
    // began — the panel simply refused to move, with nothing to see.
    gesture.current = {
      kind,
      startX: e.clientX,
      startY: e.clientY,
      from: placement,
    };
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      // Capture is an optimisation: it keeps the gesture alive when the
      // pointer leaves the element. Without it the panel's own move handler
      // still tracks the pointer while it stays inside.
    }
  };

  const onGestureMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;

    if (g.kind === "move") {
      // Clamped so the pad can never be dragged somewhere it cannot be dragged
      // back from — losing the header off-screen mid-exam is unrecoverable.
      setPlacement({
        ...g.from,
        x: Math.min(Math.max(0, g.from.x + dx), window.innerWidth - 80),
        y: Math.min(Math.max(0, g.from.y + dy), window.innerHeight - 40),
      });
    } else {
      setPlacement({
        ...g.from,
        w: Math.max(MIN_W, Math.min(g.from.w + dx, window.innerWidth - g.from.x)),
        h: Math.max(MIN_H, Math.min(g.from.h + dy, window.innerHeight - g.from.y)),
      });
    }
  };

  const endGesture = () => {
    gesture.current = null;
  };

  /* -- render -------------------------------------------------------- */

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 left-4 z-40 flex items-center gap-1.5 rounded-full border border-border-strong bg-surface px-3 py-2 text-xs font-medium text-ink shadow-lg hover:bg-surface-sunken"
      >
        <Pencil className="size-3.5" />
        Rough work
        {notes.trim() !== "" || strokes.length > 0 ? (
          // So a student who minimised it can tell there is still work inside.
          <span className="size-1.5 rounded-full bg-accent" aria-hidden />
        ) : null}
      </button>
    );
  }

  return (
    <div
      className="fixed z-40 flex flex-col overflow-hidden rounded-lg border border-border-strong bg-surface shadow-2xl"
      style={{
        left: placement.x,
        top: placement.y,
        width: placement.w,
        height: placement.h,
      }}
      onPointerMove={onGestureMove}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
    >
      <div
        className="flex shrink-0 cursor-move items-center gap-1 border-b border-border bg-surface-sunken px-2 py-1.5 select-none"
        onPointerDown={beginGesture("move")}
      >
        <GripVertical className="size-3.5 shrink-0 text-ink-faint" />
        <span className="mr-1 text-2xs font-medium text-ink-muted">
          Rough work
        </span>

        {/* Not dragged from: a pointerdown on a tab must switch it, not start
            a move gesture. */}
        <div
          className="flex items-center gap-0.5"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {(["write", "draw"] as const).map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              aria-pressed={tab === id}
              className={cn(
                "flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-2xs",
                tab === id
                  ? "bg-accent-soft font-medium text-accent"
                  : "text-ink-muted hover:bg-border/50",
              )}
            >
              {id === "write" ? (
                <Type className="size-3" />
              ) : (
                <Pencil className="size-3" />
              )}
              {id === "write" ? "Write" : "Draw"}
            </button>
          ))}
        </div>

        <div
          className="ml-auto flex items-center gap-0.5"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            title="Minimise"
            className="rounded-sm p-1 text-ink-faint hover:bg-border/50 hover:text-ink"
          >
            <Minus className="size-3.5" />
          </button>
        </div>
      </div>

      {tab === "write" ? (
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          spellCheck={false}
          placeholder="Work it out here…"
          className="min-h-0 flex-1 resize-none bg-surface p-2.5 font-mono text-xs text-ink outline-none placeholder:text-ink-faint"
        />
      ) : (
        <>
          <div
            className="flex shrink-0 items-center gap-1 border-b border-border px-2 py-1"
            onPointerDown={(e) => e.stopPropagation()}
          >
            {COLOURS.map((c) => (
              <button
                key={c}
                type="button"
                title="Pen colour"
                onClick={() => {
                  setColour(c);
                  setErasing(false);
                }}
                aria-pressed={!erasing && colour === c}
                className={cn(
                  "size-4 rounded-full border transition-transform",
                  !erasing && colour === c
                    ? "scale-110 border-ink"
                    : "border-border-strong",
                )}
                style={{ background: c }}
              />
            ))}
            <button
              type="button"
              onClick={() => setErasing((v) => !v)}
              aria-pressed={erasing}
              title="Eraser"
              className={cn(
                "ml-1 rounded-sm p-1",
                erasing
                  ? "bg-accent-soft text-accent"
                  : "text-ink-faint hover:bg-border/50 hover:text-ink",
              )}
            >
              <Eraser className="size-3.5" />
            </button>

            <button
              type="button"
              onClick={() => setStrokes((s) => s.slice(0, -1))}
              disabled={strokes.length === 0}
              title="Undo"
              className="ml-auto rounded-sm p-1 text-ink-faint hover:bg-border/50 hover:text-ink disabled:opacity-40"
            >
              <Undo2 className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setStrokes([])}
              disabled={strokes.length === 0}
              title="Clear the drawing"
              className="rounded-sm p-1 text-ink-faint hover:bg-border/50 hover:text-ink disabled:opacity-40"
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>

          <canvas
            ref={canvasRef}
            className="min-h-0 w-full flex-1 touch-none bg-surface"
            onPointerDown={startStroke}
            onPointerMove={extendStroke}
            onPointerUp={endStroke}
            onPointerLeave={endStroke}
            onPointerCancel={endStroke}
          />
        </>
      )}

      {/* Resize handle. Its own pointer capture, so a drag that starts here
          never turns into a move. */}
      <div
        onPointerDown={beginGesture("resize")}
        className="absolute right-0 bottom-0 size-4 cursor-nwse-resize"
        title="Resize"
      >
        <span className="absolute right-1 bottom-1 block size-2 border-r-2 border-b-2 border-border-strong" />
      </div>
    </div>
  );
}

/** Kept so the runner can clear the pad when an attempt finishes. */
export function clearScratchpad(attemptId: string): void {
  try {
    sessionStorage.removeItem(storageKey(attemptId));
  } catch {
    /* nothing to do */
  }
}
